const { debounce, trackGlobalSearchResults, trackGlobalSearch } = window.themeUtils;

if (!customElements.get("search-results-live")) {
    class SearchResultsLive extends HTMLElement {
        constructor() {
        super();
            
            this.announceTimer = null;
            this.cache = new Map();
            this.input = this.querySelector("[data-sr-input]");
            this.mount = this.querySelector("[data-sr-mount]");
            this.form = this.querySelector("form.ps_search");
            this.announce = this.querySelector("[data-ps-announce]");

            this.sectionId = this.getAttribute("section-id") || "search";
            this.historyMode = this.getAttribute("history") || "replace";
            this.historyParams = (this.getAttribute("history-params") || "q,type,page,sort_by").split(",");
            this.cacheTtl = Number(this.getAttribute("cache-ttl-ms") || 30000);
            this.controller = null;

            this.state = { q: "", type: "", page: 1, sort_by: "" };

            this.onInput = debounce(this.handleInput.bind(this), 300);
            this.onClick = this.handleClick.bind(this);
            this.onPopState = this.handlePopState.bind(this);
        }

        connectedCallback() {
            this.hydrateStateFromUrl();
            this.input.addEventListener("input", this.onInput);
            this.addEventListener("click", this.onClick);
            window.addEventListener("popstate", this.onPopState);

            this.querySelector("[data-ps-count]")?.remove();
        }

        disconnectedCallback() {
            this.input.removeEventListener("input", this.onInput);
            this.removeEventListener("click", this.onClick);
            window.removeEventListener("popstate", this.onPopState);
            this.controller?.abort();
        }

        hydrateStateFromUrl() {
            const params = new URLSearchParams(window.location.search);
            this.state = {
                q: params.get("q") || "",
                type: params.get("type") || "",
                page: Number(params.get("page") || 1),
                sort_by: params.get("sort_by") || "",
            };
        }

        buildUrl(forSection = true) {
            const params = new URLSearchParams();
            params.set("q", this.state.q);
            if (this.state.type) params.set("type", this.state.type);
            if (this.state.sort_by) params.set("sort_by", this.state.sort_by);
            if (this.state.page > 1) params.set("page", String(this.state.page));
            params.set("options[prefix]", "last");
            if (forSection) params.set("section_id", this.sectionId);
    
            const root = window.Shopify?.routes?.root || "/";
            return `${root}search?${params}`;
        }

        handleInput() {
            const q = this.input.value.trim();
            if (q === this.state.q) return;
    
            const wasCleared = this.state.q !== "" && q === "";
            this.state.q = q;
            this.state.page = 1;
    
            if (wasCleared) trackGlobalSearchClear(this.state.q, 0);
    
            this.refresh();
        }

        handleClick(e) {
            const pagination = e.target.closest("[data-sr-load-more]");
            if (pagination) {
                e.preventDefault();
                const href = new URL(pagination.href, window.location.origin);
                this.state.page = Number(href.searchParams.get("page") || 1);
                this.refresh({ scrollToTop: true });
            }
        }

        handlePopState() {
            this.hydrateStateFromUrl();
            this.input.value = this.state.q;
            this.refresh({ writeHistory: false });
        }

        async refresh({ writeHistory = true, scrollToTop = false } = {}) {
            this.controller?.abort();
            this.controller = new AbortController();
    
            const url = this.buildUrl(true);
            const cached = this.cache.get(url);

            this.setAttribute("data-state", "loading");
            this.mount.setAttribute("aria-busy", "true");
    
            if (cached && Date.now() - cached.at < this.cacheTtl) {
                this.render(cached.html, { writeHistory, scrollToTop });
                return;
            }
    
            try {
                const res = await fetch(url, { signal: this.controller.signal });
                if (!res.ok) throw new Error(`Search section fetch failed: ${res.status}`);
    
                const html = await res.text();
                this.cache.set(url, { html, at: Date.now() });
                this.render(html, { writeHistory, scrollToTop });
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") return;
    
                console.error("[search-results-live]", err);
                this.setAttribute("data-state", "error");
                this.mount.setAttribute("aria-busy", "false");
            }
        }

        render(html, opts) {
            const next = new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector("[data-sr-mount]");
    
            if (!next) {
                console.error("[search-results-live] no results mount in section response");
                return;
            }

            const countText = next.querySelector("[data-ps-count]");
            const announcementText = countText?.textContent.trim() || "";
            countText?.remove();

            this.mount.innerHTML = next.innerHTML;
    
            this.setAttribute("data-state", "ready");
            this.mount.setAttribute("aria-busy", "false");
    
            if (opts.writeHistory) this.syncHistory();
            if (opts.scrollToTop) this.scrollIntoView({ behavior: "smooth", block: "start" });
    
            const count = this.results().length;
            this.setLiveRegionText(announcementText);
    
            trackGlobalSearchResults(this.state.q, count);
        }

        results() {
            return Array.from(
                this.mount.querySelectorAll("[data-ps-result]")
            );
        }

        syncHistory() {
            const params = new URLSearchParams(window.location.search);
    
            for (const key of this.historyParams) {
                const value = this.state[key];
                if (value === "" || value === 0 || value === 1 || value == null) {
                    params.delete(key);
                } else {
                    params.set(key, String(value));
                }
            }
    
            const next = `${window.location.pathname}?${params}`;
            const method = this.historyMode === "push" ? "pushState" : "replaceState";
            window.history[method]({ search: this.state }, "", next);
        }

        setLiveRegionText(announcementText){
            if (this.announce) {
              clearTimeout(this.announceTimer);
              this.announceTimer = setTimeout(() => {
                this.announce.textContent = announcementText;
              }, 300);
            }
        }
    }

  customElements.define("search-results-live", SearchResultsLive);
}